<script setup lang="ts">
    import { useHipSpineXRayModalViewModel } from '@/views/3d-planner/hipSpine/xrayModal/useHipSpineXRayModalViewModel';
    import HipSpineLandmarkAnnotations from '@/views/3d-planner/hipSpine/components/HipSpineLandmarkAnnotations.vue';
    import HipSpineAngleList from '@/views/3d-planner/hipSpine/components/HipSpineAngleList.vue';
    import { HIP_SPINE_TARGETS } from '@/domain/analytics/analyticsTargets';

    const vm = useHipSpineXRayModalViewModel();
</script>

<template>
    <v-card-item class="angles-annotations-container pa-3">
        <v-tabs
            v-model="vm.activePanelView"
            class="panel-tabs"
            color="primary"
            grow
            :data-analytics-id="HIP_SPINE_TARGETS.measurementsTabGroup"
        >
            <v-tab value="angles" :data-analytics-id="HIP_SPINE_TARGETS.anglesTab"> Angles </v-tab>
            <v-tab value="annotations" :data-analytics-id="HIP_SPINE_TARGETS.annotationsTab">
                Annotations
            </v-tab>
        </v-tabs>
        <v-divider />
        <div class="angles-annotations-content d-flex flex-column">
            <v-window v-model="vm.activePanelView" class="flex-grow-1 w-100 overflow-auto">
                <v-window-item value="angles">
                    <HipSpineAngleList v-if="vm.activePostureId" />
                </v-window-item>
                <v-window-item value="annotations">
                    <HipSpineLandmarkAnnotations
                        v-if="vm.activePostureId"
                        :posture="vm.activePostureId"
                    />
                </v-window-item>
            </v-window>
            <div class="w-100 flex-shrink-0">
                <v-alert v-if="vm.hasImageError" type="error" variant="tonal" density="compact">
                    There was an error loading the X-ray for this posture.
                </v-alert>
                <v-alert
                    v-else-if="vm.hasLandmarkError"
                    type="error"
                    variant="tonal"
                    density="compact"
                >
                    There was an error loading the landmarks for this posture.
                </v-alert>
                <div
                    class="angles-annotations-control"
                    :data-analytics-id="HIP_SPINE_TARGETS.approveButtonGroup"
                >
                    <app-button-primary
                        variant="outlined"
                        color="primary"
                        class="text-none w-33"
                        data-testid="hip-spine-reset-to-ai"
                        :disabled="vm.isResetDisabled"
                        :loading="vm.isResetting"
                        @click="vm.handleReset"
                    >
                        Reset to AI
                    </app-button-primary>
                    <app-button-cta
                        class="w-66"
                        :prepend-icon="vm.isActivePostureApproved ? 'mdi-check' : undefined"
                        :disabled="vm.isApproveDisabled"
                        :loading="vm.isApproving"
                        @click="vm.handleApprove"
                    >
                        {{
                            vm.isActivePostureApproved
                                ? 'Annotations Approved'
                                : 'Approve Annotations'
                        }}
                    </app-button-cta>
                </div>
                <v-alert
                    v-if="vm.approveError"
                    type="error"
                    variant="tonal"
                    density="compact"
                    class="mt-2"
                >
                    {{ vm.approveError }}
                </v-alert>
                <v-alert
                    v-if="vm.resetError"
                    type="error"
                    variant="tonal"
                    density="compact"
                    class="mt-2"
                >
                    {{ vm.resetError }}
                </v-alert>
            </div>
        </div>
    </v-card-item>
</template>

<style scoped lang="scss">
    .angles-annotations-container {
        width: 320px;
    }

    .angles-annotations-content {
        height: calc(100vh - 290px);
        justify-content: space-between;
        overflow-y: auto;
    }

    .angles-annotations-control {
        display: flex;
        justify-content: space-between;
        gap: 10px;
        width: calc(100% - 7px);
    }

    .panel-tabs {
        :deep(.v-tab) {
            text-transform: capitalize;
            font-size: 14px;
        }
    }

    .v-alert {
        font-size: 14px;

        :deep(.v-alert__prepend) {
            grid-row: 1 / -1;
            align-self: center;
        }
    }
</style>
